import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { QueryFactory } from '@lmt/shared-types';
import {
  GDTMappingListDto,
  PatchGDTMappingDto,
  SortDirection,
} from '@lmt/shared-types';

import {
  getSortIcon,
  ScrollToTop,
} from '@lmt/unicorn-shared/app/components/common/table/helper';
import { Sorting } from '@lmt/unicorn-shared/app/components/common/table/types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';

const StyledTableCell = (props: any) => (
  <TableCell
    {...props}
    style={{ margin: 0.5, paddingLeft: 14, paddingTop: 16, paddingBottom: 16 }}
  />
);

type Props = {
  noResults: boolean;
  data: GDTMappingListDto[];
  changeSorting: (sorting: Sorting) => void;
};

const nameof = nameofFactory<GDTMappingListDto>();

const columnStyles = {
  [nameof('name')]: {
    minWidth: '60px',
    width: '80%',
    sortable: true,
  },
  ['actions']: {
    minWidth: '60px',
    width: '10%',
    sortable: false,
  },
};

export const MappingsListTable: FC<Props> = React.memo(
  function MappingsTable(props) {
    const i18next = useScopedTranslation('gdt.mapping.list');
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(
      null,
    );
    const queryCache = useQueryClient();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    function changeSorting(field: string) {
      if (field === sortField) {
        if (sortDirection === SortDirection.Asc) {
          props.changeSorting({
            field: field,
            direction: SortDirection.Desc,
          });
          setSortDirection(SortDirection.Desc);
        } else {
          props.changeSorting({
            direction: undefined,
            field: undefined,
          });
          setSortField(null);
          setSortDirection(null);
        }
      } else {
        props.changeSorting({
          field: field,
          direction: SortDirection.Asc,
        });
        setSortField(field);
        setSortDirection(SortDirection.Asc);
      }
    }

    const renameMapping = useCallback(
      async (configuration: GDTMappingListDto) => {
        try {
          const newName = await modals.showPrompt({
            defaultValue: configuration.name,
            text: i18next.t('rename.text'),
            title: i18next.t('rename.title'),
          });
          if (newName) {
            await ClientFactory.GDTMappingClient.patch(configuration.id, {
              name: newName,
            });
            await queryCache.invalidateQueries({
              queryKey: QueryFactory.GDTMappingQuery.getAllQueryKey(),
            });
          }
        } catch (e) {
          modals.showError({ text: convertToErrorString(e) });
        }
      },
      [],
    );
    const deleteMapping = useCallback(async (mapping: GDTMappingListDto) => {
      try {
        if (
          await modals.showConfirm({
            title: i18next.t('delete.confirm_title'),
            text: i18next.t('delete.confirm_text', mapping),
          })
        )
          await ClientFactory.GDTMappingClient.remove(mapping.id);
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.GDTMappingQuery.getAllQueryKey(),
        });
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    }, []);

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18next.t('empty_list_text')}
          </div>
        </div>
      );
    }
    return (
      <TableContainer
        ref={tableRef}
        className={styles.tableContainer}
        component={Paper}
      >
        <Table stickyHeader className={styles.table}>
          <TableHead>
            <TableRow>
              {Object.keys(columnStyles).map((columnKey) => (
                <StyledTableCell
                  key={`${columnKey}`}
                  onClick={() =>
                    columnStyles[columnKey].sortable && changeSorting(columnKey)
                  }
                  className={
                    columnStyles[columnKey].sortable
                      ? styles.sortableHeader
                      : styles.header
                  }
                  style={columnStyles[columnKey]}
                >
                  {columnStyles[columnKey].sortable ? (
                    <div className={styles.headerCell}>
                      {i18next.t(`columns.${columnKey}` as any)}
                      {getSortIcon(
                        sortField === columnKey ? sortDirection : null,
                      )}
                    </div>
                  ) : (
                    <div className={styles.headerCell}>
                      {i18next.t(`columns.${columnKey}` as any)}
                    </div>
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((mapping) => (
              <TableRow key={mapping.id} hover>
                <StyledTableCell>
                  <div>{mapping.name}</div>
                </StyledTableCell>
                <StyledTableCell>
                  <div>
                    <Button
                      color={ButtonColor.Secondary}
                      title={i18next.t('rename_button')}
                      onClick={() => renameMapping(mapping)}
                    />
                    <Button
                      color={ButtonColor.Danger}
                      title={i18next.t('delete_button')}
                      onClick={() => deleteMapping(mapping)}
                    />
                  </div>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
);
